//Oct27 backup SubmissionTable.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Skeleton from "@mui/material/Skeleton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Button,
  Tooltip,
} from "@mui/material";
import { useFormSubmissions } from "../../contexts/FormSubmissionsContext";
import styles from "./submissionTable.module.css";
import { safeSetItem, safeGetItem } from "../../utils/storageUtils";
import { useFormContext } from "../../contexts/FormContext";
import { useFormSubmissionSettings } from "../../contexts/FormSubmissionSettingsContext"; 
import { useFormLayout } from "../../contexts/FormLayoutContext";                   
import { useFieldMetadata } from "../../contexts/FieldMetadataContext";                

import SubmissionTableZeroState from "./SubmissionTableZeroState";
import SubmissionTableToolbar from './SubmissionTableToolbar';

import { useAssetPreview } from "../../utils/useAssetPreview";
import {
  unwrapValue,
  getLinkedForRow,
  extractFirstAssetId,
  parseVirtualSpec,
} from "../../utils/assetCellHelpers";
import {
  getProviderColumnDisplayState,
} from "../../utils/providerOutputValueUtils";
import {
  getProviderColumnDefinition,
} from "../../integrations/providerColumns";
import {
  BITLY_CLICKS_TOTAL_COLUMN_ID,
  BITLY_METRIC_KEYS,
  BITLY_PROVIDER_ID,
} from "../../integrations/providers/bitly/bitlyProviderColumns";
import {
  syncBitlySubmissionClicks,
} from "../Integrations/integrationFunctions";
import { useAuth } from "../../contexts/AuthContext"; 
import { isSystemId, resolveSystemValue, getSystemComparator } from "../../utils/systemColumns";

const MIN_COLUMN_WIDTH = 50; // Minimum column width
const DEFAULT_COLUMN_WIDTH = 150; // Default width for columns

// Utility function to generate default column widths
const getDefaultColumnWidths = (columns) =>
  columns.reduce((acc, col) => ({ ...acc, [col.id]: DEFAULT_COLUMN_WIDTH }), {});

// Default thumbnail size (in px) for asset cells.
// Kept at 18px for backwards compatibility; SubmissionsPage can override via prop.
const DEFAULT_THUMB_SIZE = 40;

/* ---------------------------------------------------------------- helpers (shared with DisplayTable) */
const buildFromLayout = (layoutConfig = {}, meta = {}) =>
  Object.values(layoutConfig)
    .flat()
    .filter((f) => f.type !== 'submit')
    .map((f, idx) => ({
      id     : meta[f.id]?.ufi   ?? f.id,
      label  : meta[f.id]?.label ?? `Field ${idx + 1}`,
      visible: true,
      order  : idx,
    }));

// Normalizes persisted columnOrder so the table can treat every item as an object.
// Keeps any extra keys on virtual rows (kind/role/ufi/label/visible/order).
const normalizeColPrefs = (order = []) => {
  return (Array.isArray(order) ? order : [])
    .filter(Boolean)
    .map((c, i) => {
      if (typeof c === 'string') {
        // string → assume it's an id (e.g., 'thumb@<ufi>'); default visible/order
        return { id: c, visible: true, order: i };
      }
      // object → ensure required fields are present; keep metadata (kind/role/ufi/aufUfi…)
      return {
        ...c,
        id: String(c.id || ''),
        visible: c.visible !== false,
        order: Number.isFinite(c.order) ? c.order : i,
      };
    });
};

const PROVIDER_VIRTUAL_ROLES = new Set([
  'providerOutput',
  'providerMetric',
]);

const ASSET_PREVIEW_VIRTUAL_ROLES = new Set([
  'thumb',
  'version',
  'assetType',
  'assetId',
  'aufField',
]);

const isProviderVirtualRole = (role) =>
  PROVIDER_VIRTUAL_ROLES.has(String(role || ''));

const isAssetPreviewVirtualRole = (role) =>
  ASSET_PREVIEW_VIRTUAL_ROLES.has(String(role || ''));

/**
 * Enrich provider-owned persisted column rows from the provider registry.
 *
 * Enterprise note:
 * - This keeps provider-specific labels and value paths out of SubmissionTable.
 * - If a persisted column is only stored as "providerOutput@bitly@shortUrl",
 *   the registry can still provide the user-facing label and metadata.
 * - This does not call provider APIs or fetch anything. It only decorates the
 *   already-loaded column model for rendering.
 */
const enrichProviderColumnMetadata = (column = {}) => {
  const spec = parseVirtualSpec(column);

  if (!isProviderVirtualRole(spec?.role)) {
    return column;
  }

  const providerColumn = {
    ...column,
    ...spec,
    kind: column.kind || 'virtual',
  };

  const definition = getProviderColumnDefinition(providerColumn);

  if (!definition) {
    return providerColumn;
  }

  return {
    ...definition,
    ...providerColumn,
    id: providerColumn.id || definition.id,
    label: providerColumn.label || definition.label || providerColumn.id || definition.id,
    visible: providerColumn.visible !== false,
    order: Number.isFinite(providerColumn.order)
      ? providerColumn.order
      : definition.order,
  };
};

const isHttpUrlValue = (value) => {
  const text = String(value ?? '').trim();

  if (!/^https?:\/\//i.test(text)) {
    return false;
  }

  try {
    const parsed = new URL(text);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (_) {
    return false;
  }
};

const getRowCopyIdentity = (row = {}) => {
  return (
    row?.id ||
    row?.submissionId ||
    row?.submissionDocId ||
    'unknown-row'
  );
};

const buildCellCopyId = ({ row, columnId, text }) => {
  return [
    getRowCopyIdentity(row),
    String(columnId || 'unknown-column'),
    String(text || ''),
  ].join('::');
};

const normalizeProviderColumnForRuntime = (column = {}) => {
  const spec = parseVirtualSpec(column);

  if (!isProviderVirtualRole(spec?.role)) {
    return null;
  }

  return {
    ...column,
    ...spec,
    kind: column.kind || 'virtual',
  };
};

const getProviderCellState = (row, column = {}) => {
  const providerColumn = normalizeProviderColumnForRuntime(column);

  if (!providerColumn) {
    return null;
  }

  const definition = getProviderColumnDefinition(providerColumn);

  return getProviderColumnDisplayState(
    row,
    providerColumn,
    definition
  );
};

const pushSearchToken = (tokens, value) => {
  if (value === null || value === undefined) return;

  if (typeof value?.toDate === 'function') {
    try {
      const d = value.toDate();
      tokens.push(d.toISOString());
      tokens.push(d.toLocaleString());
      tokens.push(d.toLocaleDateString());
      tokens.push(d.toLocaleTimeString());
    } catch (_) {}
    return;
  }

  if (value instanceof Date) {
    tokens.push(value.toISOString());
    tokens.push(value.toLocaleString());
    tokens.push(value.toLocaleDateString());
    tokens.push(value.toLocaleTimeString());
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => pushSearchToken(tokens, item));
    return;
  }

  if (typeof value === 'object') {
    if ('value' in value) {
      pushSearchToken(tokens, value.value);
      return;
    }

    Object.values(value).forEach((item) => pushSearchToken(tokens, item));
    return;
  }

  tokens.push(String(value));
};

const getProviderSortValue = (row, column = {}) => {
  const state = getProviderCellState(row, column);

  if (!state) {
    return '';
  }

  return state.value ?? state.displayText ?? '';
};

const compareSortableValues = (valA, valB, order) => {
  const normalize = (value) => {
    const unwrapped = unwrapValue(value);

    if (unwrapped === null || unwrapped === undefined) {
      return '';
    }

    if (typeof unwrapped?.toDate === 'function') {
      try {
        return unwrapped.toDate().getTime();
      } catch (_) {
        return '';
      }
    }

    if (unwrapped instanceof Date) {
      return unwrapped.getTime();
    }

    if (typeof unwrapped === 'object') {
      if ('value' in unwrapped) {
        return normalize(unwrapped.value);
      }

      return '';
    }

    return unwrapped;
  };

  const a = normalize(valA);
  const b = normalize(valB);

  const numA = parseFloat(a);
  const numB = parseFloat(b);

  if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
    return order === 'asc' ? numA - numB : numB - numA;
  }

  const strA = a == null ? '' : String(a);
  const strB = b == null ? '' : String(b);

  return order === 'asc'
    ? strA.localeCompare(strB)
    : strB.localeCompare(strA);
};

const PROVIDER_METRIC_SYNC_STALE_MS = 30 * 60 * 1000;
const PROVIDER_METRIC_SYNC_LIMIT = 25;

const getTimestampMillis = (value) => {
  if (!value) return 0;

  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate().getTime();
    } catch (_) {
      return 0;
    }
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? 0 : value.getTime();
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  }

  if (typeof value?.seconds === 'number') {
    return value.seconds * 1000;
  }

  if (typeof value?._seconds === 'number') {
    return value._seconds * 1000;
  }

  return 0;
};

const getSubmissionDocIdForProviderMetricSync = (row = {}) => {
  const id = row?.id ? String(row.id) : '';

  if (id && !id.startsWith('local_')) {
    return id;
  }

  const submissionDocId = row?.submissionDocId ?
    String(row.submissionDocId) :
    '';

  if (submissionDocId && !submissionDocId.startsWith('local_')) {
    return submissionDocId;
  }

  return null;
};

const getRowOrganizationId = (row = {}) => {
  return row.organizationId || null;
};

const SUPPORTED_BITLY_PROVIDER_METRIC_KEYS = new Set([
  BITLY_METRIC_KEYS.TOTAL_CLICKS,
  BITLY_METRIC_KEYS.REFRESHED_AT,
]);

const isProviderMetricColumn = (column = {}) => {
  const spec = parseVirtualSpec(column);

  return (
    spec?.role === 'providerMetric' ||
    column.role === 'providerMetric'
  );
};

const isSupportedBitlyProviderMetricColumn = (column = {}) => {
  if (column.id === BITLY_CLICKS_TOTAL_COLUMN_ID) {
    return true;
  }

  const spec = parseVirtualSpec(column);

  const role = spec?.role || column.role;
  const provider = spec?.provider || column.provider;
  const metricKey = spec?.metricKey || column.metricKey;

  return (
    role === 'providerMetric' &&
    provider === BITLY_PROVIDER_ID &&
    SUPPORTED_BITLY_PROVIDER_METRIC_KEYS.has(metricKey)
  );
};

const isBitlyClicksMetricFresh = (row = {}) => {
  const syncedAtMillis = getTimestampMillis(
    row.bitlyClicksSyncedAt ||
    row.providerOutputs?.bitly?.metrics?.clicksSyncedAt ||
    row.shortlinks?.bitly?.clicks?.syncedAt
  );

  if (syncedAtMillis > Date.now() - PROVIDER_METRIC_SYNC_STALE_MS) {
    return true;
  }

  const attemptedAtMillis = getTimestampMillis(
    row.bitlyClicksAttemptedAt ||
    row.shortlinks?.bitly?.clicks?.attemptedAt
  );

  const status =
    row.bitlyClicksStatus ||
    row.shortlinks?.bitly?.clicks?.status ||
    null;

  return (
    attemptedAtMillis > Date.now() - PROVIDER_METRIC_SYNC_STALE_MS &&
    (
      status === 'rate_limited' ||
      status === 'error'
    )
  );
};

const buildProviderMetricPatchMap = (updates = []) => {
  return (updates || []).reduce((acc, update) => {
    if (!update?.submissionId || !update.patch) {
      return acc;
    }

    acc[update.submissionId] = update.patch;
    return acc;
  }, {});
};

const getColumnHeaderHelperText = (column = {}) => {
  if (column.helperText) {
    return column.helperText;
  }

  const providerColumn = normalizeProviderColumnForRuntime(column);

  if (!providerColumn) {
    return null;
  }

  const definition = getProviderColumnDefinition(providerColumn);

  return (
    definition?.helperText ||
    definition?.description ||
    null
  );
};


// Function to highlight the search query in the displayed text
const highlightMatch = (text, queries) => {
  if (!text || !queries.length || queries.every(q => q.trim() === "")) {
    return text; // ✅ Fix: If no valid search terms, return normal text without highlighting
  }

  // Escape special characters for regex & create a case-insensitive match for all terms
  const regex = new RegExp(
    `(${queries
      .filter(q => q.trim() !== "") // ✅ Ignore empty queries to prevent space highlighting
      .map(q => q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join("|")})`,
    "gi"
  );

  return text.split(regex).map((part, index) =>
    queries.some(query => part.toLowerCase() === query.toLowerCase()) ? (
      <span key={index} className={styles.highlightText}>{part}</span>
    ) : (
      part
    )
  );
};

const SubmissionTable = ({ thumbSize = DEFAULT_THUMB_SIZE }) => {
const { formId, formIdReady } = useFormContext();


const {
  submissions,
  rowCount,          
  loading,
  isTestingMode,
  hasMoreSubmissions,
  loadMoreSubmissions,
  updatePageSize,
  setDateRange,
  dateRange,
  patchSubmissionsById,
} = useFormSubmissions();
  const storageKey = formId ? `submission_${formId}_tableSettings` : "submission_default_tableSettings";
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // ─────────────────────────────────────────────────────────────
  // ASSET PREVIEW HOOK + AUTH TOKEN GETTER + TYPE MAP
  // ─────────────────────────────────────────────────────────────
  const { primePreviews, getPreview } = useAssetPreview();

  // ✅ Use your real AuthContext
  const { getToken: getTokenFromAuth, currentUser } = useAuth() || {};

  // Unified token getter used by useAssetPreview priming
  const getAuthToken = useCallback(
    async () => {
      try {
        // Prefer AuthContext.getToken (it already handles refresh safely)
        if (typeof getTokenFromAuth === "function") {
          return await getTokenFromAuth(false); // no force-refresh by default
        }
        // Fallback: use Firebase user directly if present
        if (currentUser?.getIdToken) {
          return await currentUser.getIdToken();
        }
        return null;
      } catch (e) {
        console.warn("[SubmissionTable] getAuthToken failed:", e?.message || e);
        return null;
      }
    },
    [getTokenFromAuth, currentUser]
  );

  const { fieldsMetadata } = useFieldMetadata();

  // Build a quick UFI → type map so we can detect base asset fields (attach/image)
  const ufiToType = useMemo(() => {
    const map = {};
    try {
      Object.values(fieldsMetadata || {}).forEach((m) => {
        if (m?.ufi) map[m.ufi] = m.type || "text";
      });
    } catch (e) {
      console.warn("[ST:typeMap] failed to build ufiToType:", e?.message || e);
    }
    return map;
  }, [fieldsMetadata]);

  // ─────────────────────────────────────────────────────────────
  // High-performance row search index (system + fields + assets)
  // - Caches per row object via WeakMap → O(1) reuse across renders
  // - Rebuilds only when fieldsMetadata changes (attach/image UFI set)
  // - Produces a lower-cased token array covering:
  //     • System: id/submissionId, submitterId, submitterSessionId, organizationId, submittedAt (human + ISO)
  //     • Field responses (strings/numbers)
  //     • Asset/virtual: assetId, assetType, versionNumber, AUF responses (from _linkedAssetsJson)
  // ─────────────────────────────────────────────────────────────
  const rowIndexCacheRef = useRef(
    /** @type {{ map: WeakMap<object,string[]> }} */ ({ map: new WeakMap() })
  );

  const providerSearchColumnsRef = useRef([]);

  // Invalidate the cache whenever field definitions change (UFIs/attach fields may change)
  useEffect(() => {
    rowIndexCacheRef.current.map = new WeakMap();
  }, [fieldsMetadata]);

  const buildRowSearchIndex = useCallback((row) => {
    if (!row || typeof row !== 'object') return [];

    // 1) Return cached index if present
    const cached = rowIndexCacheRef.current.map.get(row);
    if (cached) return cached;

    const tokens = [];

    // (A) System / top-level fields (multiple date string forms)
    try {
      const sys = [
        row?.id || row?.submissionId || '',
        row?.submitterId || '',
        row?.submitterSessionId || '',
        row?.organizationId || '',
      ];

      if (row?.submittedAt?.toDate) {
        const d = row.submittedAt.toDate();
        sys.push(
          d.toISOString(),
          d.toLocaleString(),
          d.toLocaleDateString(),
          d.toLocaleTimeString()
        );
      }
      sys.filter(Boolean).forEach(s => tokens.push(String(s)));
    } catch (_) {}

    // (B) Responses (existing data path)
    try {
      Object.values(row?.responses || {}).forEach((resp) => {
        const v0 = (resp && typeof resp === 'object' && 'value' in resp) ? resp.value : resp;
        const v = unwrapValue(v0);
        if (v == null) return;
        if (Array.isArray(v)) {
          v.forEach(x => x != null && tokens.push(String(x)));
        } else if (typeof v === 'object') {
          if (typeof v.value !== 'object' && v.value != null) tokens.push(String(v.value));
        } else {
          tokens.push(String(v));
        }
      });
    } catch (_) {}

    // (C) Asset/virtual fields: assetId, assetType, versionNumber, AUF responses
    try {
      // Collect attach/image UFIs from metadata (once per build)
      const assetUfIs = [];
      Object.values(fieldsMetadata || {}).forEach(m => {
        if (m?.ufi && (m.type === 'attach' || m.type === 'image')) assetUfIs.push(m.ufi);
      });

      // First asset id per attach/image UFI (from responses)
      assetUfIs.forEach(ufi => {
        const aid = extractFirstAssetId(row, ufi);
        if (aid) tokens.push(String(aid));
      });

      // Use authoritative linked snapshot when present
      const linked = getLinkedForRow(row); // Map<attachUfi, entry> | null
      if (linked && typeof linked.forEach === 'function') {
        linked.forEach((entry) => {
          if (!entry) return;
          if (entry.assetId)               tokens.push(String(entry.assetId));
          if (entry.assetType)             tokens.push(String(entry.assetType));
          if (entry.versionNumber != null) tokens.push(String(entry.versionNumber));

          const ar = entry.aufResponses || {};
          Object.values(ar).forEach(val => {
            if (val == null) return;
            if (Array.isArray(val)) val.forEach(x => x != null && tokens.push(String(x)));
            else tokens.push(String(val));
          });
        });
      }
    } catch (_) {}

    // (D) Provider-owned enrichment output:
    // - Bitly Short URL today
    // - Future Bitly metrics / HubSpot / Slack / BigQuery output later
    //
    // Enterprise note:
    // This reads already-fetched row data only through the provider registry.
    // It does not call provider APIs, add Firestore listeners, or hard-code
    // provider row paths in the table.
    try {
      (providerSearchColumnsRef.current || []).forEach((providerColumn) => {
        const state = getProviderCellState(row, providerColumn);

        if (!state) return;

        pushSearchToken(tokens, state.value);
        pushSearchToken(tokens, state.displayText);
        pushSearchToken(tokens, state.status);
        pushSearchToken(tokens, state.error);
      });
    } catch (_) {}

    // Normalize to lower-case once; cache & return
    const idx = tokens
      .filter(Boolean)
      .map(s => String(s).toLowerCase());

    rowIndexCacheRef.current.map.set(row, idx);
    return idx;
  }, [fieldsMetadata]);


  // Pull shared settings + live layout/meta (same inputs as DisplayTable)
  const {
    submissionSettings,
    updateTableSurfaceSettings,
  } = useFormSubmissionSettings(); // ← shared slice
  const { layoutConfig } = useFormLayout();

// Fallback columns used when layout/meta isn't ready yet (prevents 0-col remount loop)
const FALLBACK_COLUMNS = React.useMemo(() => ([
  { id: 'sys:submittedAt',  label: 'Submitted', visible: true, order: 0 },
  { id: 'sys:submissionId', label: 'ID',        visible: true, order: 1 },
]), []);

const columns = React.useMemo(() => {
  // 1) Build fresh columns from live layout + metadata (REAL fields only)
  const fresh = buildFromLayout(layoutConfig, fieldsMetadata);

  // GUARD 1: avoid 0-column initial render (AUF / cold-start)
  if (!fresh || fresh.length === 0) {
    console.warn("[ST:columns] layout/meta not ready; using fallback system columns.");
    return FALLBACK_COLUMNS;
  }

  // 2) Normalize any saved designer prefs (may be strings or objects with virtuals)
  const rawPref =
    submissionSettings?.submissionsPageTable?.columnOrder ??
    submissionSettings?.addToSubmissionsTable?.columnOrder ??
    [];

  const pref = normalizeColPrefs(rawPref);

  // 3) If no prefs yet → just use fresh
  if (pref.length === 0) {
    console.log("[ST:columns] built", {
      freshCount: fresh.length,
      savedOrderCount: 0,
      mergedCount: fresh.length,
    });
    return fresh;
  }

  // 4) Build a fast label lookup for REAL fields (virtuals keep their own label if provided)
  const labelById = new Map(fresh.map(f => [f.id, f.label]));

  // 5) Start from prefs (this preserves order, visibility, and any virtual rows)
  // Provider-owned virtual columns are enriched from the provider registry so
  // SubmissionTable does not hard-code provider-specific labels or schemas.
  let base = pref.map((c, i) => {
    const normalizedColumn = {
      ...c,
      label: c.label || labelById.get(c.id) || c.id,
      order: Number.isFinite(c.order) ? c.order : i,
    };

    return enrichProviderColumnMetadata(normalizedColumn);
  });

  // 6) Append any brand-new REAL fields not present in prefs
  const baseIds = new Set(base.map(c => c.id));
  fresh.forEach(f => {
    if (!baseIds.has(f.id)) {
      base.push({
        id: f.id,
        label: f.label,
        visible: true,
        order: base.length,
      });
    }
  });

  // 7) Final stable order by `order`
  base = base
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // GUARD 2: still empty? fall back to system columns
  if (!base || base.length === 0) {
    console.warn("[ST:columns] merged model empty; using fallback system columns.");
    return FALLBACK_COLUMNS;
  }

  console.log("[ST:columns] built", {
    freshCount: fresh.length,
    savedOrderCount: rawPref.length,
    mergedCount: base.length,
  });

  return base;
}, [layoutConfig, fieldsMetadata, submissionSettings, FALLBACK_COLUMNS]);


// Keep provider search/sort metadata aligned with the active table model.
//
// Enterprise note:
// - Provider-specific value paths stay in provider adapters.
// - The search index reads through getProviderColumnDisplayState.
// - Invalidate row search cache when provider columns change so searches
//   immediately reflect Bitly/provider output availability.
useEffect(() => {
  providerSearchColumnsRef.current = (columns || [])
    .map((column) => normalizeProviderColumnForRuntime(column))
    .filter(Boolean);

  rowIndexCacheRef.current.map = new WeakMap();

  console.log('[ST:providerSearchColumns] refreshed provider search columns:', {
    count: providerSearchColumnsRef.current.length,
    ids: providerSearchColumnsRef.current.map((column) => column.id),
  });
}, [columns]);




 // State management
const [order, setOrder] = useState("asc");
const [orderBy, setOrderBy] = useState(columns.length > 0 ? columns[0].id : null);
const [page, setPage] = useState(0);
const [filteredData, setFilteredData] = useState(submissions);
const [searchQuery, setSearchQuery] = useState([]);   // ← now an array
const [columnOrder, setColumnOrder] = useState(columns); // ← start with merged columns
const [copiedCellId, setCopiedCellId] = useState(null);
const [initialFetchDone, setInitialFetchDone] = useState(false);
const [providerMetricsRefreshing, setProviderMetricsRefreshing] = useState(false);
const providerMetricSyncInFlightRef = useRef(false);
const providerMetricSyncSeenRef = useRef(new Map());

// Ensure `orderBy` set once on first mount to sys:submittedAt DESC,
// even if that column is hidden, and only change it later if the user
// picks a different column or the key truly disappears AND isn’t a sys:* key.
const sortInitRef = useRef(false);

useEffect(() => {
  if (!columns || columns.length === 0) return;

  if (!sortInitRef.current) {
    console.log('[ST:sort:init] defaulting to sys:submittedAt DESC regardless of visibility');
    setOrderBy('sys:submittedAt');
    setOrder('desc');
    sortInitRef.current = true;
    return;
  }

  // If current sort key vanishes from the model, *but* it is a sys:* key,
  // keep it — we can still sort rows by top-level fields even if the column is hidden.
  if (orderBy && !isSystemId(String(orderBy)) && !columns.some((c) => c.id === orderBy)) {
    console.log('[ST:sort] current non-system sort key not in model → fallback to first column (keep current direction)');
    setOrderBy(columns[0]?.id || null);
  }
}, [columns, orderBy]);



// Adopt the model produced by settings + layout so UI reflects changes immediately
useEffect(() => {
  if (!columns?.length) return;
  console.log("[ST:columns] adopting latest columns model from settings/layout", {
    count: columns.length,
    ids: columns.map(c => c.id),
  });
  setColumnOrder(columns);
}, [columns]);



/* ------------------------------------------------------------------ */
/* Track when the FIRST snapshot arrives (rows or empty).             */
/*  - This prevents "stuck skeleton" on legitimately empty pages.     */
/*  - We keep using initialFetchDone for UI gating; this ref is       */
/*    diagnostic and consistent with logs.                            */
/* ------------------------------------------------------------------ */
const hasLoadedOnceRef = useRef(false);
useEffect(() => {
  if (!hasLoadedOnceRef.current && !loading) {
    hasLoadedOnceRef.current = true;
    console.log("[ST:firstPage] hasLoadedOnce=true (first snapshot received)");
  }
}, [loading]);



useEffect(() => {
  /* We only care about the very first response from Firestore.
     When `loading` becomes false the first time, we know the
     initial slice is in `submissions` and `rowCount` is set.   */
  if (!loading && !initialFetchDone) {
    console.log("[SubmissionTable] First Firestore page has arrived.");
    setInitialFetchDone(true);
  }
}, [loading, initialFetchDone]);

/**
 * SINGLE EFFECT to handle column settings:
 * - Only runs if we have `formId` (so we have a valid storageKey)
 * - Only runs if submissions are loaded (submissions.length > 0)
 * - Loads from localStorage if available, otherwise calls resetToDefaults
 */
const columnsRef = useRef([]);
const hasLoadedColumnsRef = useRef(false); // ✅ Prevent multiple resets
 const submissionsRef = useRef(submissions);
 useEffect(() => {
   submissionsRef.current = submissions;
 }, [submissions]);

useEffect(() => {
  console.log(
    `[SubmissionTable: debug] Attempting to load column settings for form: ${formId}`
  );

  // If no formId or still no submissions, do nothing (prevents repeated calls)
  if (!formId) {
    console.log(
      "[SubmissionTable: debug] No formId yet, skipping column settings load."
    );
    return;
  }

  // 1) “Derived” baseline is now our merged DisplayTable columns
  const derivedColumns = columns;

  console.log("[SubmissionTable] Derived columns:", derivedColumns);

  // 2) Check if derivedColumns changed from the last time
  if (JSON.stringify(columnsRef.current) !== JSON.stringify(derivedColumns)) {
    console.log(
      "[SubmissionTable] New column structure detected. Updating reference..."
    );
    columnsRef.current = derivedColumns;
  }

  // Prevent multiple resets by checking `hasLoadedColumnsRef`
  if (hasLoadedColumnsRef.current) {
    console.log(
      "[SubmissionTable] Column settings already loaded. Skipping reset."
    );
    return;
  }

  // 3) First, see if shared settings already have an order/widths (preferred)
 const sharedOrder  =
   submissionSettings?.submissionsPageTable?.columnOrder ??
   submissionSettings?.addToSubmissionsTable?.columnOrder ??
   [];
 const sharedWidths =
   submissionSettings?.submissionsPageTable?.columnWidths ??
   submissionSettings?.addToSubmissionsTable?.columnWidths ??
   {};

  if (sharedOrder.length) {
    console.log("[SubmissionTable] Applying shared (form-wide) columnOrder from settings slice.");
    const normalizedShared = normalizeColPrefs(sharedOrder);

    // 🔒 Purge legacy top-level system ids; ColumnDraftManager emits only sys:* now
    const LEGACY_IDS = new Set([
      "submittedAt",
      "submissionId",
      "submitterId",
      "submitterSessionId",
      "organizationId",
    ]);
    const sharedPurged = normalizedShared.filter(c => !LEGACY_IDS.has(c.id));
    if (sharedPurged.length !== normalizedShared.length) {
      console.warn("[SubmissionTable] Purged legacy ids from shared columnOrder:", {
        removed: normalizedShared.filter(c => LEGACY_IDS.has(c.id)).map(c => c.id),
      });
    }

    setColumnOrder(sharedPurged);

    if (Object.keys(sharedWidths).length) {
      console.log("[SubmissionTable] Setting column widths from saved settings...");
      setColumnWidths((prev) => ({ ...prev, ...sharedWidths }));
      setLocalColumnWidths((prev) => ({ ...prev, ...sharedWidths }));
    }
    hasLoadedColumnsRef.current = true;
    return;
  }



// unwrap storageUtils’ { value, timestamp } if present
const unwrapStored = (x) =>
  x && typeof x === 'object' && Object.prototype.hasOwnProperty.call(x, 'value')
    ? x.value
    : x;

// 4) Otherwise, attempt to load saved settings from local storage (compat path)
const savedSettings = unwrapStored(safeGetItem(storageKey));
console.log("[SubmissionTable] Retrieved saved settings:", savedSettings);

const storedOrder  = savedSettings?.columnOrder || [];
const storedWidths = savedSettings?.columnWidths || {};

const normalizedStored = normalizeColPrefs(storedOrder);

/* ──────────────────────────────────────────────────────────
 * ▶▶▶  GUARD — wipe cache if storedOrder is incompatible   ◀◀◀
 * ────────────────────────────────────────────────────────── */
const orderIsCompatible =
  normalizedStored.length > 0 &&
  normalizedStored.every((col) =>
    derivedColumns.some((dc) => dc.id === col.id)
  );

  if (!orderIsCompatible) {
    console.warn(
      "[SubmissionTable] Saved columnOrder is out‑of‑date – clearing cache & rebuilding defaults."
    );

    // 1) Remove the bad cache from localStorage
    localStorage.removeItem(storageKey);

    // 2) If you also persist per‑user settings in Firestore, call
    //    your helper here, e.g.  clearColumnOrderInFirestore(formId, userId);

    // 3) Rebuild defaults from live data
    resetToDefaults();
    hasLoadedColumnsRef.current = true;
    return; // 🚪 Exit effect early – everything is now reset
  }
  /* ────────────────────────────────────────────────────────── */

  // 5) If no saved settings, reset to defaults
  if (!savedSettings || (storedOrder.length === 0 && derivedColumns.length > 0)) {
    console.log(
      "[SubmissionTable] No valid settings found. Resetting to defaults..."
    );
    resetToDefaults();
    hasLoadedColumnsRef.current = true; // ✅ Mark as loaded after resetting defaults
    return;
  }

  // 6) Load saved settings (purge any legacy ids first)
  const LEGACY_IDS = new Set([
    "submittedAt",
    "submissionId",
    "submitterId",
    "submitterSessionId",
    "organizationId",
  ]);
  const storedPurged = normalizedStored.filter(c => !LEGACY_IDS.has(c.id));
  if (storedPurged.length !== normalizedStored.length) {
    console.warn("[SubmissionTable] Purged legacy ids from local columnOrder:", {
      removed: normalizedStored.filter(c => LEGACY_IDS.has(c.id)).map(c => c.id),
    });
  }

  if (JSON.stringify(storedPurged) !== JSON.stringify(columnOrder)) {
    console.log("[SubmissionTable] Setting column order from saved settings (purged)...");
    setColumnOrder(storedPurged);
  } else {
    console.log("[SubmissionTable] Column order already matches. No update needed.");
  }



  if (Object.keys(storedWidths).length > 0) {
    console.log("[SubmissionTable] Setting column widths from saved settings...");
    setColumnWidths(storedWidths);
    setLocalColumnWidths(storedWidths);
  } else {
    console.log(
      "[SubmissionTable] No storedWidths found. Possibly defaults were used earlier."
    );
  }

  // ✅ Mark as loaded to prevent redundant resets
  hasLoadedColumnsRef.current = true;
}, [formId, storageKey, columns, submissionSettings]);


/**
 * Recompute `filteredData` whenever:
 *   • a new Firestore page arrives  (`submissions` changes)
 *   • the user adds / removes chips  (`searchQuery` changes)
 *   • the visible-column set changes (`columnOrder` changes)
 *
 * Uses the cached per-row index (system + fields + assets).
 */
useEffect(() => {
  if (!Array.isArray(submissions)) return;

  // no rows → zero state will handle
  if (submissions.length === 0) {
    console.warn("[SubmissionTable] No submissions found. Switching to zero state.");
    setFilteredData([]);
    return;
  }

  // Normalize chip queries once
  const qTokens = (searchQuery || [])
    .map(s => (s == null ? "" : String(s).trim().toLowerCase()))
    .filter(Boolean);

  // No active chips → expose all rows
  if (qTokens.length === 0) {
    console.log("[ST:filter] no chips → expose all rows", { rows: submissions.length });
    setFilteredData(submissions);
    return;
  }

  // Match if ANY chip appears in the row’s cached index
  const fresh = submissions.filter((row) => {
    const idx = buildRowSearchIndex(row); // ← from Step 1
    if (!idx || idx.length === 0) return false;
    return qTokens.some(q => idx.some(tok => tok.includes(q)));
  });

  console.log("[ST:filter] chips active (index-based)", {
    chips: qTokens,
    matched: fresh.length,
    total: submissions.length
  });

  setFilteredData(fresh);
}, [submissions, searchQuery, columnOrder, buildRowSearchIndex]);


useEffect(() => {
  const maxRowIndexOnPage = page * rowsPerPage;          // first row shown on this page
  if (searchQuery.length > 0 && maxRowIndexOnPage >= filteredData.length) {
    setPage(0);                                          // snap back to the first page
  }
}, [
  searchQuery.length,        // any time chips are added/removed
  filteredData.length,       // any time the match count changes
  page,                      // so we re-evaluate when the user paginates
  rowsPerPage                // needed if the user changes the page-size dropdown
]);

  const sharedWidths =
    submissionSettings?.submissionsPageTable?.columnWidths ||
    submissionSettings?.addToSubmissionsTable?.columnWidths ||
    {};

  const initialWidths = useMemo(
    () => columns.reduce((acc, c) => ({ ...acc, [c.id]: sharedWidths[c.id] ?? DEFAULT_COLUMN_WIDTH }), {}),
    [columns, sharedWidths]
  );
  const [columnWidths, setColumnWidths] = useState(initialWidths);
  const [localColumnWidths, setLocalColumnWidths] = useState(initialWidths);

  // ─────────────────────────────────────────────────────────────────────────────
  // Keep width maps in sync if columns change (add defaults, prune orphaned IDs)
  // Mirrors the DisplayTable behavior so new columns get widths and deleted ones
  // don’t leave stale entries behind.
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    setColumnWidths((prev) => {
      const next = { ...prev };
      // add missing
      columns.forEach((c) => { if (!(c.id in next)) next[c.id] = DEFAULT_COLUMN_WIDTH; });
      // prune deleted
      Object.keys(next).forEach((id) => {
        if (!columns.some((c) => c.id === id)) delete next[id];
      });

   if (Object.keys(prev).length !== Object.keys(next).length) {
     console.log("[ST:widths:colWidths]", { prevKeys: Object.keys(prev), nextKeys: Object.keys(next) });
   }

      return next;
    });
    setLocalColumnWidths((prev) => {
      const next = { ...prev };
      columns.forEach((c) => { if (!(c.id in next)) next[c.id] = DEFAULT_COLUMN_WIDTH; });
      Object.keys(next).forEach((id) => {
        if (!columns.some((c) => c.id === id)) delete next[id];
      });

   if (Object.keys(prev).length !== Object.keys(next).length) {
     console.log("[ST:widths:localColWidths]", { prevKeys: Object.keys(prev), nextKeys: Object.keys(next) });
   }
      return next;
    });
  }, [columns]);


  const [resizing, setResizing] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isBlurTriggered, setIsBlurTriggered] = useState(false); // Detects click-away to save data

  // Pagination Handlers
 const handleChangePage = (event, newPage) => {
   setPage(newPage);

  /* ★ If the user just navigated to, or past, the last locally
       loaded row—and we know there are more rows on the server—
       fetch the next Firestore page. */
  const maxRowIndexRequested = (newPage + 1) * rowsPerPage;
 console.log("[ST:page] change", { newPage, maxRowIndexRequested, localRows: submissions.length, hasMoreSubmissions });

 if (hasMoreSubmissions && maxRowIndexRequested > submissions.length) {
   loadMoreSubmissions();
 }
 };

 const handleChangeRowsPerPage = (event) => {
   const newSize = parseInt(event.target.value, 10);
   setRowsPerPage(newSize);
   setPage(0);

  /* ★ Tell the context to use the new Firestore page size, then
       make sure we have at least one full page loaded. */
  updatePageSize(newSize);

 console.log("[ST:rowsPerPage] change", { newSize, localRows: submissions.length, hasMoreSubmissions });

 if (hasMoreSubmissions && submissions.length < newSize) {
     loadMoreSubmissions();
   }
 };

// Show the initial skeleton only until the very first query completes.
// Do NOT couple to submissions.length to avoid "empty page = skeleton forever".
const isInitialLoading = !formIdReady || !initialFetchDone;

  const haveAnyRows = rowCount > 0;
  const isRowCountKnown = rowCount >= 0; // -1 means the aggregate count hasn't arrived yet

 // ─────────────────────────────────────────────────────────────
 // Stabilize rowCount so it never goes "backward" to -1 mid-session.
 // This prevents gate re-evaluations (and flicker) during prefetch or context flips.
 // ─────────────────────────────────────────────────────────────
 const lastKnownCountRef = useRef(null);
 useEffect(() => {
   if (rowCount >= 0) {
     lastKnownCountRef.current = rowCount;
   }
 }, [rowCount]);

 const effectiveRowCount =
   isRowCountKnown ? rowCount :
   (lastKnownCountRef.current !== null ? lastKnownCountRef.current : -1);


// Filters hydration: guard zero/no-matches until dateRange is known (Toolbar sets default Last 7 Days on mount)
const filtersHydratedRef = useRef(false);
useEffect(() => {
  // Consider filters hydrated when dateRange is a 2-entry array of Dates
  const ok =
    Array.isArray(dateRange) &&
    dateRange.length === 2 &&
    dateRange[0] instanceof Date &&
    dateRange[1] instanceof Date;
  if (ok) filtersHydratedRef.current = true;
  console.log("[ST:filtersHydrated]", { ok, dateRange });
}, [dateRange]);

const displayedSubmissions = useMemo(() => 
  isTestingMode ? submissions : submissions || [], 
  [submissions, isTestingMode]
);

// Derive the rows we should render:
// • When there are active chips, use filteredData
// • Otherwise, use submissions directly to avoid a blank table during hydration
const renderRows = useMemo(
  () => (searchQuery.length > 0 ? filteredData : submissions),
  [searchQuery.length, filteredData, submissions]
);

// ─────────────────────────────────────────────────────────────
// Sorting function (hoisted declaration to avoid TDZ anywhere)
// and memoized sorted rows to avoid re-sorting every render.
// PLACE THIS BLOCK RIGHT AFTER THE renderRows useMemo ABOVE.
// ─────────────────────────────────────────────────────────────
function sortData(rows, orderBy, order) {
  if (!orderBy || rows.length === 0) return rows; // ✅ Prevent sorting errors while ensuring zero state shows

  // System columns (ColumnDraftManager emits ids like 'sys:*')
  if (isSystemId(String(orderBy))) {
    const cmp = getSystemComparator(order, orderBy);
    return [...rows].sort(cmp);
  }

  const sortColumn =
    (columnOrder || []).find((column) => column?.id === orderBy) ||
    (columns || []).find((column) => column?.id === orderBy) ||
    { id: orderBy };

  const sortSpec = parseVirtualSpec(sortColumn);

  // Provider-owned virtual columns:
  // - Bitly Short URL today
  // - Future Bitly metrics / HubSpot / Slack / BigQuery output later
  //
  // Enterprise note:
  // This sorts already-fetched row data only through the provider registry.
  // It does not call provider APIs, add Firestore listeners, or hard-code
  // provider row paths in the table.
  if (isProviderVirtualRole(sortSpec?.role)) {
    const providerColumn = {
      ...sortColumn,
      ...sortSpec,
      kind: sortColumn.kind || 'virtual',
    };

    return [...rows].sort((a, b) => {
      const valA = getProviderSortValue(a, providerColumn);
      const valB = getProviderSortValue(b, providerColumn);

      return compareSortableValues(valA, valB, order);
    });
  }

  return [...rows].sort((a, b) => {

    // unwrap like the cell renderer does
    const rawA = a?.responses?.[orderBy];
    const rawB = b?.responses?.[orderBy];
    const valA0 = (rawA && typeof rawA === "object" && "value" in rawA) ? rawA.value : rawA;
    const valB0 = (rawB && typeof rawB === "object" && "value" in rawB) ? rawB.value : rawB;
    const valA = unwrapValue(valA0);
    const valB = unwrapValue(valB0);

    return compareSortableValues(valA, valB, order);
  });
}


// ✅ Memoize sorted rows so we don't re-run sort() on every render.
//    This also lets us reuse the same sorted set for page-slicing and asset priming.
const sortedRows = useMemo(
  () => sortData(renderRows, orderBy, order),
  [renderRows, orderBy, order, columnOrder, columns]
);


// ─────────────────────────────────────────────────────────────
// Guard against a momentary empty column model during hydration
// Show a small skeleton instead of flashing an empty table.
// ─────────────────────────────────────────────────────────────
const visibleColumns = useMemo(
  () => (columnOrder || []).filter((c) => c && c.visible !== false),
  [columnOrder]
);

// Optional micro-opt: precompute spec-by-id for visible columns
const virtualSpecById = useMemo(() => {
  const map = new Map();
  (visibleColumns || []).forEach(c => {
    // ⬇️ pass the entire column object so object-style virtuals are recognized
    map.set(c.id, parseVirtualSpec(c));
  });
  return map;
}, [visibleColumns]);


// ─────────────────────────────────────────────────────────────
// PRIME ASSET PREVIEWS for visible asset-ish columns on page
//   • Virtual:  thumb@<attachUfi>, version@<attachUfi>, assetType@<attachUfi>,
//               aufField@<attachUfi>@<aufUfi>, assetId@<attachUfi>
//   • Base:     columns whose UFI type is attach/image
// ─────────────────────────────────────────────────────────────
useEffect(() => {
  const pageSlice = renderRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  if (pageSlice.length === 0) return;

  const ids = [];

  // A) asset preview virtual columns: derive attachUfi via parseVirtualSpec.
  // Provider-owned virtual columns are intentionally excluded here because
  // they do not need asset preview priming.
  const virtualCols = (visibleColumns || []).filter((c) => {
    const spec = virtualSpecById.get(c.id);
    return !!spec?.role && isAssetPreviewVirtualRole(spec.role);
  });

  // B) base asset columns (attach/image)
  const baseAssetCols = (visibleColumns || []).filter((c) => {
    const t = ufiToType[c.id];
    return t === "attach" || t === "image";
  });

  if (virtualCols.length === 0 && baseAssetCols.length === 0) return;

  pageSlice.forEach((row) => {
    if (!row) return;

// virtual columns
for (const col of virtualCols) {
  const spec = virtualSpecById.get(col.id);
  if (!spec?.ufi) continue; // use spec.ufi consistently
  const aid = extractFirstAssetId(row, spec.ufi);
  if (aid) ids.push(aid);
}


    // base asset fields (id is the UFI)
    for (const col of baseAssetCols) {
      const aid = extractFirstAssetId(row, col.id);
      if (aid) ids.push(aid);
    }
  });

  const uniq = [...new Set(ids)];
  if (uniq.length) {
    primePreviews(uniq, getAuthToken);
  }
}, [visibleColumns, virtualSpecById, ufiToType, renderRows, page, rowsPerPage, primePreviews, getAuthToken]);


// ─────────────────────────────────────────────────────────────
// Provider metric refresh/sync for visible table rows.
//
// Enterprise rules:
// - The table never calls provider APIs directly.
// - The table calls only backend-owned integration functions.
// - The sync scope is the visible page after date range/search/pagination.
// - Backend skips fresh rows and writes cached provider metrics to Firestore.
// - The UI patches returned cached fields without a full table refetch.
// - The toolbar action stays provider-neutral for future integrations.
// ─────────────────────────────────────────────────────────────
const hasVisibleProviderMetricColumns = useMemo(() => {
  return (visibleColumns || []).some((column) => {
    return isProviderMetricColumn(column);
  });
}, [visibleColumns]);

const hasVisibleSupportedProviderMetricSync = useMemo(() => {
  return (visibleColumns || []).some((column) => {
    return isSupportedBitlyProviderMetricColumn(column);
  });
}, [visibleColumns]);

const visibleProviderMetricRows = useMemo(() => {
  return sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
}, [
  sortedRows,
  page,
  rowsPerPage,
]);

const syncVisibleProviderMetrics = useCallback(async ({
  forceRefresh = false,
  reason = 'auto_visible_provider_metric_sync',
} = {}) => {
  if (!formId) {
    return {
      ok: false,
      reason: 'missing_form_id',
    };
  }

  if (!hasVisibleSupportedProviderMetricSync) {
    return {
      ok: true,
      reason: 'no_supported_visible_provider_metrics',
    };
  }

  if (
    !Array.isArray(visibleProviderMetricRows) ||
    visibleProviderMetricRows.length === 0
  ) {
    return {
      ok: true,
      reason: 'no_visible_rows',
    };
  }

  if (typeof patchSubmissionsById !== 'function') {
    console.warn(
      '[SubmissionTable][providerMetrics] patchSubmissionsById is unavailable.'
    );

    return {
      ok: false,
      reason: 'patch_helper_unavailable',
    };
  }

  if (providerMetricSyncInFlightRef.current) {
    return {
      ok: true,
      reason: 'sync_already_in_flight',
    };
  }

  const syncableRows = visibleProviderMetricRows
    .filter((row) => Boolean(getSubmissionDocIdForProviderMetricSync(row)))
    .filter((row) => Boolean(getRowOrganizationId(row)))
    .filter((row) => {
      return forceRefresh || !isBitlyClicksMetricFresh(row);
    })
    .slice(0, PROVIDER_METRIC_SYNC_LIMIT);

  if (syncableRows.length === 0) {
    console.log('[SubmissionTable][providerMetrics] No stale visible metrics to sync:', {
      formId,
      reason,
      forceRefresh,
      visibleRows: visibleProviderMetricRows.length,
    });

    return {
      ok: true,
      reason: 'no_stale_visible_metrics',
    };
  }

  const organizationId = getRowOrganizationId(syncableRows[0]);

  if (!organizationId) {
    return {
      ok: false,
      reason: 'missing_organization_id',
    };
  }

  const submissionIds = syncableRows
    .map((row) => getSubmissionDocIdForProviderMetricSync(row))
    .filter(Boolean);

  if (submissionIds.length === 0) {
    return {
      ok: true,
      reason: 'no_submission_ids',
    };
  }

  const syncKey = [
    formId,
    organizationId,
    page,
    rowsPerPage,
    submissionIds.join(','),
    forceRefresh ? 'force' : 'stale',
  ].join('::');

  const lastSyncAt = providerMetricSyncSeenRef.current.get(syncKey) || 0;

  if (
    !forceRefresh &&
    lastSyncAt > Date.now() - PROVIDER_METRIC_SYNC_STALE_MS
  ) {
    return {
      ok: true,
      reason: 'recently_requested',
    };
  }

  providerMetricSyncSeenRef.current.set(syncKey, Date.now());
  providerMetricSyncInFlightRef.current = true;
  setProviderMetricsRefreshing(true);

  console.log('[SubmissionTable][providerMetrics] Sync requested:', {
    formId,
    organizationId,
    reason,
    forceRefresh,
    page,
    rowsPerPage,
    submissionCount: submissionIds.length,
    submissionIds: submissionIds.slice(0, 10),
  });

  try {
    const result = await syncBitlySubmissionClicks({
      organizationId,
      formId,
      submissionIds,
      staleAfterMinutes: 30,
      limit: PROVIDER_METRIC_SYNC_LIMIT,
      forceRefresh,
    });

    const updates = Array.isArray(result?.updates) ?
      result.updates :
      [];

    const patchMap = buildProviderMetricPatchMap(updates);

    if (Object.keys(patchMap).length > 0) {
      patchSubmissionsById(patchMap);
    }

    console.log('[SubmissionTable][providerMetrics] Sync completed:', {
      formId,
      organizationId,
      reason,
      requestedCount: result?.requestedCount || 0,
      syncedCount: result?.syncedCount || 0,
      skippedCount: result?.skippedCount || 0,
      errorCount: result?.errorCount || 0,
      patchedCount: Object.keys(patchMap).length,
    });

    return result;
  } catch (error) {
    console.warn('[SubmissionTable][providerMetrics] Sync failed:', {
      formId,
      organizationId,
      reason,
      message: error?.message || error,
    });

    return {
      ok: false,
      reason: 'sync_failed',
      error,
    };
  } finally {
    providerMetricSyncInFlightRef.current = false;
    setProviderMetricsRefreshing(false);
  }
}, [
  formId,
  hasVisibleSupportedProviderMetricSync,
  page,
  patchSubmissionsById,
  rowsPerPage,
  visibleProviderMetricRows,
]);

useEffect(() => {
  if (!hasVisibleSupportedProviderMetricSync) {
    return;
  }

  syncVisibleProviderMetrics({
    forceRefresh: false,
    reason: 'auto_visible_provider_metric_column',
  });
}, [
  hasVisibleSupportedProviderMetricSync,
  syncVisibleProviderMetrics,
]);

const handleRefreshProviderMetrics = useCallback(() => {
  return syncVisibleProviderMetrics({
    forceRefresh: true,
    reason: 'toolbar_manual_provider_metric_refresh',
  });
}, [
  syncVisibleProviderMetrics,
]);


// GATES SNAPSHOT (one consolidated log before branching)
console.log("[ST:GATES]", {
  formIdReady,
  loading,
  hasLoadedOnce: hasLoadedOnceRef.current,
  isInitialLoading,
  rowCount,
  isRowCountKnown,
  effectiveRowCount,
  displayedLen: displayedSubmissions.length,
  filtersHydrated: filtersHydratedRef.current,
});

// Enterprise empty-state policy:
//
// The submissions table is always date-scoped because the toolbar initializes
// a default date range. Therefore, effectiveRowCount === 0 means "no rows in
// the current view", not necessarily "this form has never had submissions".
//
// Without a separate all-time submission count, the safest enterprise UX is to
// keep the toolbar visible and show an empty-current-view state so users can
// widen the date range or clear search filters.
const emptyCurrentViewReady =
  initialFetchDone &&
  filtersHydratedRef.current &&
  !loading &&
  renderRows.length === 0 &&
  (
    effectiveRowCount === 0 ||
    searchQuery.length > 0
  );


// Column order updates from Toolbar/Drawer — persist shared + local storage + local state
const handleColumnOrderChange = useCallback((updatedColumnOrder) => {
  console.log(`[SubmissionTable] Updating column order...`);

  // 1) Update local state immediately so UI reflects the change
  setColumnOrder(updatedColumnOrder);

  // 2) Save updated settings to storage (compat path)
  console.log(`[SubmissionTable] Saving updated column order to storage: ${storageKey}`);
  safeSetItem(storageKey, { columnOrder: updatedColumnOrder });

  // 3) Persist to shared (form-wide) table-surface settings so all consumers align
  try {
    updateTableSurfaceSettings('submissionsPageTable', { columnOrder: updatedColumnOrder });
  } catch (e) {
    console.warn("[SubmissionTable] Failed to persist shared columnOrder:", e?.message || e);
  }
}, [storageKey, updateTableSurfaceSettings]);



/* ------------------------------------------------------------------ */
/* Reset every “Manage Columns” setting back to a fresh default       */
/* ------------------------------------------------------------------ */
const resetToDefaults = () => {
  console.log("[SubmissionTable] Resetting to default column settings…");

  // Guard: prefer the merged column model (layout+meta merged with saved order).
  // This matches the DisplayTable baseline and works even when there are no rows yet.
  if (!columns || columns.length === 0) {
    console.warn(
      "[SubmissionTable] Attempted to reset defaults but we have no columns from layout/meta. Skipping…"
    );
    return;
  }

  /* -------------------------------------------------------------- */
  /* (1)  Build fresh column list                                   */
  /* -------------------------------------------------------------- */

  // (1-a) The baseline is our merged DisplayTable columns (already includes
  //       real fields + any saved order; we also appended top-level fields).
  //       No need to re-derive from submissions here.
  const defaultColumns = columns;

  /* -------------------------------------------------------------- */
  /* (2)  Build default order / widths                              */
  /* -------------------------------------------------------------- */
  const defaultColumnOrder = defaultColumns.map((col, idx) => ({
    ...col,
    visible: true, // every column visible when resetting
    order  : idx,
  }));

  const defaultColumnWidths = getDefaultColumnWidths(defaultColumns);

  /* -------------------------------------------------------------- */
  /* (3)  Commit to state and storage                               */
  /* -------------------------------------------------------------- */
  setColumnOrder(defaultColumnOrder);
  setColumnWidths(defaultColumnWidths);
  setLocalColumnWidths(defaultColumnWidths);

  console.log(
    `[SubmissionTable] Clearing stored settings under key: ${storageKey}`
  );
  safeSetItem(storageKey, {
    columnOrder : defaultColumnOrder,
    columnWidths: defaultColumnWidths,
  });

  // Also persist to the shared (form-wide) table-surface settings slice so this page stays in sync
  try {
    updateTableSurfaceSettings('submissionsPageTable', {
      columnOrder : defaultColumnOrder,
      columnWidths: defaultColumnWidths,
    });
  } catch (e) {
    console.warn("[SubmissionTable] Failed to persist shared defaults:", e?.message || e);
  }
};



// Start column resizing
const startResizing = (columnId, event) => {
  if (isResizing) {
    console.warn(`[startResizing: WARNING] Resize attempt blocked as resizing is already active.`);
    return;
  }

  console.group(`[🔍 startResizing] Resizing column: '${columnId}'`);

  const startWidth = localColumnWidths[columnId] || DEFAULT_COLUMN_WIDTH;

  console.log(`[startResizing: DEBUG] Starting width from 'localColumnWidths' for '${columnId}': ${startWidth}px`);

  setResizing({
    columnId,
    startX: event.clientX,
    startWidth
  });

  setIsResizing(true);
  setIsBlurTriggered(false);
  console.groupEnd();

};

const handleMouseMove = (event) => {
  if (!resizing) return;
  const { columnId, startX, startWidth } = resizing;

  const deltaX = event.clientX - startX;
  let newWidth = Math.max(MIN_COLUMN_WIDTH, startWidth + deltaX);

  // 🔥 Optimization: Only update if the new width is different
  setLocalColumnWidths((prevWidths) => {
    if (prevWidths[columnId] === newWidth) return prevWidths;
    return { ...prevWidths, [columnId]: newWidth };
  });

  // Force cell resizing in real-time for header + all cells in that column
  const allCells = document.querySelectorAll(`[data-column-id='${columnId}']`);
  allCells.forEach((cell) => {
    cell.style.width = `${newWidth}px`;
    cell.style.minWidth = `${MIN_COLUMN_WIDTH}px`;
  });
};

// Stop resizing
const stopResizing = () => {
  const columnId = resizing?.columnId;
  console.group(`[🔍 stopResizing] Stopping resize for column: '${columnId || 'N/A'}'`);


  if (resizing && columnId) {
    setColumnWidths((prevWidths) => {
      const updatedWidths = {
        ...prevWidths,
        [columnId]: localColumnWidths[columnId],
      };

      console.log(`[stopResizing] Saving updated column widths to storage:`, updatedWidths);
      safeSetItem(storageKey, { columnOrder, columnWidths: updatedWidths });

      // Also persist to the shared (form-wide) table-surface settings slice for this page
      try {
        updateTableSurfaceSettings('submissionsPageTable', { columnWidths: updatedWidths });
      } catch (e) {
        console.warn("[stopResizing] Failed to persist shared columnWidths:", e?.message || e);
      }

      return updatedWidths;
    });
  }


  setResizing(null);
  setIsResizing(false);

  console.groupEnd();
};




const totalRowsForPagination = useMemo(() => {
  // • show “matches” while searching
  if (searchQuery.length > 0) return filteredData.length;

  // • when not searching, prefer server aggregate; fallback to local rows
  return rowCount > -1 ? rowCount : submissions.length;
}, [
  searchQuery.length,
  filteredData.length,
  rowCount,
  submissions.length,
]);




// Optimize the resizing effect (attach stable handlers while resizing)
useEffect(() => {
  if (!isResizing) return;

  const onMove = (e) => handleMouseMove(e);
  const onUp   = () => stopResizing();
  const onBlur = () => stopResizing();

  document.addEventListener("mousemove", onMove);
  document.addEventListener("mouseup", onUp);
  document.addEventListener("blur", onBlur);

  return () => {
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseup", onUp);
    document.removeEventListener("blur", onBlur);
  };
}, [isResizing, handleMouseMove]); // `handleMouseMove` is stable in this file


// ★ Prefetch the next page when we’re within ~50% of the page from the end
useEffect(() => {
  if (loading || !hasMoreSubmissions) return;
  const threshold = submissions.length - Math.ceil(rowsPerPage * 0.5);
  const endIndex  = (page + 1) * rowsPerPage;
 console.log("[ST:prefetch:check]", { endIndex, threshold, localRows: submissions.length, hasMoreSubmissions });

  if (endIndex >= threshold) {
    // console.log("[SubmissionTable] Prefetching next page (near end of local window)...");
    loadMoreSubmissions();
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [page, rowsPerPage, submissions.length, hasMoreSubmissions, loading, loadMoreSubmissions]);


// ✅ Optimize displayedSubmissions Effect to Prevent Redundant Renders
useEffect(() => {
  if (!resizing) {
console.log("[SubmissionTable] rows memo:", {
  submissionsLen: submissions.length,
  filteredLen: filteredData.length,
  renderRowsLen: renderRows.length,
  pageSlice: [page * rowsPerPage, page * rowsPerPage + rowsPerPage],
});
    console.log("[SubmissionTable] Checking columnOrder:", columnOrder);
  }
}, [displayedSubmissions, columnOrder]); // ✅ Removed unrelated states like columnWidths to prevent extra triggers



const handleSearch = useCallback((queries, filters) => {
  console.log("[handleSearch] Search Triggered:", { queries, filters, submissions });

  // Store chips for the background effect & UI highlighting
  setSearchQuery(queries);

  // Normalize queries once
  const qTokens = (queries || [])
    .map(s => (s == null ? "" : String(s).trim().toLowerCase()))
    .filter(Boolean);

  // Early-out: no chips → let background effect show all rows
  if (qTokens.length === 0) {
    setFilteredData(submissions);
    return;
  }

  // Optional: apply additional column-specific filters first (keep your logic)
  const hasFilters = filters && Object.keys(filters).length > 0;

  const filtered = submissions.filter((row) => {
    // Column filters (unchanged)
    if (hasFilters) {
      const ok = Object.keys(filters).every(
        (colId) => !filters[colId] || row.responses?.[colId]?.value === filters[colId]
      );
      if (!ok) return false;
    }

    // Index-based chip match
    const idx = buildRowSearchIndex(row); // ← from Step 1
    if (!idx || idx.length === 0) return false;
    return qTokens.some(q => idx.some(tok => tok.includes(q)));
  });

  console.log("[handleSearch] Filtered Results (index-based):", filtered);
  setFilteredData(filtered);
}, [submissions, buildRowSearchIndex]);



   const handleDateRangeChange = (newRange) => {
     setDateRange(newRange);   // triggers context → Firestore
     setPage(0);               // UX: jump back to page 1
   };
   
  /**
   * Show skeleton ONLY on the *first* paint (initial fetch),
   * or while filters aren’t hydrated *and* we have no local rows yet.
   * Do NOT show skeleton for background prefetch (rowCount == -1).
   */
if (
  isInitialLoading ||
  (!filtersHydratedRef.current && displayedSubmissions.length === 0)
) {
  console.log("[SubmissionTable] Loading gate → skeleton (initial only / filters not hydrated).");
  return (
    <Paper className={styles.tableContainer} sx={{ p: 0 }}>
      {/* Keep toolbar mounted so its effects fire once and don't remount */}
      <SubmissionTableToolbar
        columns={columns}
        data={[]}                       // nothing yet
        columnOrder={columnOrder}
        setColumnOrder={handleColumnOrderChange}
        resetToDefaults={resetToDefaults}
        onSearch={handleSearch}
        onDateRangeChange={handleDateRangeChange}
        storageKey={storageKey}
        hasVisibleProviderMetrics={hasVisibleSupportedProviderMetricSync}
        providerMetricsRefreshing={providerMetricsRefreshing}
        onRefreshProviderMetrics={handleRefreshProviderMetrics}
      />
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {Array.from({ length: Math.max(visibleColumns.length || 5, 5) }).map((_, index) => (
                <TableCell key={index}>
                  <Skeleton variant="text" width={100} height={30} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: rowsPerPage }).map((_, rowIndex) => (
              <TableRow key={rowIndex} sx={{ height: "40px" }}>
                {Array.from({ length: Math.max(visibleColumns.length || 5, 5) }).map((_, colIndex) => (
                  <TableCell key={colIndex}>
                    <Skeleton variant="rectangular" width="100%" height={30} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}





// B) Empty current view — stable and filter-aware.
// Keep the toolbar mounted so users can widen the date range, clear search,
// manage columns, export the current view, or refresh provider metrics when
// provider metric columns are visible.
if (emptyCurrentViewReady) {
  console.log("[SubmissionTable] Empty current view (stable).", {
    effectiveRowCount,
    searchTerms: searchQuery,
    hasVisibleProviderMetricColumns,
    hasVisibleSupportedProviderMetricSync,
  });

  return (
    <Paper className={styles.tableContainer} sx={{ p: 0 }}>
      <SubmissionTableToolbar
        columns={columns}
        data={filteredData}
        columnOrder={columnOrder}
        setColumnOrder={handleColumnOrderChange}
        resetToDefaults={resetToDefaults}
        onSearch={handleSearch}
        onDateRangeChange={handleDateRangeChange}
        storageKey={storageKey}
        hasVisibleProviderMetrics={hasVisibleSupportedProviderMetricSync}
        providerMetricsRefreshing={providerMetricsRefreshing}
        onRefreshProviderMetrics={handleRefreshProviderMetrics}
      />

      <SubmissionTableZeroState
        embedded
        variant="emptyView"
        title={
          searchQuery.length > 0
            ? "No submissions match your search"
            : "No submissions in this date range"
        }
        description={
          searchQuery.length > 0
            ? "Try clearing search terms or widening the date range."
            : "Try widening the date range to include older submissions."
        }
      />
    </Paper>
  );
}


if (!visibleColumns.length && isInitialLoading) {
  console.log("[ST:columns] visibleColumns empty during initial load → header skeleton");
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {Array.from({ length: 5 }).map((_, index) => (
              <TableCell key={index}>
                <Skeleton variant="text" width={100} height={30} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: rowsPerPage }).map((_, rowIndex) => (
            <TableRow key={rowIndex} sx={{ height: "40px" }}>
              {Array.from({ length: 5 }).map((_, colIndex) => (
                <TableCell key={colIndex}>
                  <Skeleton variant="rectangular" width="100%" height={30} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}


  // Handle sorting when clicking column headers
  const handleRequestSort = (columnId) => {
    setOrder((prevOrder) => {
      if (orderBy === columnId) {
        return prevOrder === "asc" ? "desc" : "asc";
      }
      return "asc"; // new column starts asc
    });
    if (orderBy !== columnId) {
      setOrderBy(columnId);
    }
  };


// Copy-to-Clipboard Function with Tooltip Feedback
const handleCopy = (text, cellCopyId) => {
  const safeText = String(text ?? '');
  const nextCopiedCellId = cellCopyId || `global::${safeText}`;

  if (!navigator?.clipboard?.writeText) {
    console.error("[handleCopy] Clipboard API is not available.");
    return;
  }

  navigator.clipboard.writeText(safeText)
    .then(() => {
      console.log(`[handleCopy] Copied to clipboard: ${safeText}`);
      setCopiedCellId(nextCopiedCellId); // Trigger tooltip for this cell only
      setTimeout(() => {
        setCopiedCellId((current) =>
          current === nextCopiedCellId ? null : current
        );
      }, 1500); // Reset tooltip after 1.5 seconds
    })
    .catch((err) => console.error("[handleCopy] Failed to copy text:", err));
};

const renderCopyableText = ({
  text,
  row,
  column,
  forceCopy = false,
  copyable = false,
}) => {
  const safeText = String(text ?? '');
  const shouldRenderAsCopy =
    Boolean(safeText) &&
    (
      forceCopy ||
      copyable ||
      isHttpUrlValue(safeText)
    );

  if (!shouldRenderAsCopy) {
    return highlightMatch(safeText, searchQuery);
  }

  const cellCopyId = buildCellCopyId({
    row,
    columnId: column?.id,
    text: safeText,
  });

  const isCopied = copiedCellId === cellCopyId;

  return (
    <Tooltip title={isCopied ? "Copied!" : "Click to copy"} placement="top" arrow>
      <span
        onClick={(e) => {
          e.stopPropagation();
          handleCopy(safeText, cellCopyId);
        }}
        style={{
          display: "inline-block",
          width: "100%",
          cursor: "pointer",
          color: isCopied ? "#1976d2" : "inherit",
        }}
      >
        {highlightMatch(safeText, searchQuery)}
      </span>
    </Tooltip>
  );
};


console.log("[SubmissionTable] Final path → RENDER TABLE", {
  rowsOnPage: Math.min(renderRows.length - page * rowsPerPage, rowsPerPage),
  renderLen: renderRows.length,
  orderBy, order,
  visibleCols: columnOrder.filter(c => c.visible !== false).map(c => c.id)
});


return (
  <Paper 
    className={styles.tableContainer} 
    sx={{ padding: 0 }} 
  >
    <SubmissionTableToolbar
      columns={columns}
      data={filteredData}  
      columnOrder={columnOrder}
      setColumnOrder={handleColumnOrderChange}
      resetToDefaults={resetToDefaults}
      onSearch={handleSearch} 
      onDateRangeChange={handleDateRangeChange}
      storageKey={storageKey}
      hasVisibleProviderMetrics={hasVisibleSupportedProviderMetricSync}
      providerMetricsRefreshing={providerMetricsRefreshing}
      onRefreshProviderMetrics={handleRefreshProviderMetrics}
    />
    <TableContainer
      style={{
        overflowX: "auto", 
      }}
      sx={{ minHeight: '80vh' }}
    >
      <Table
        style={{
          tableLayout: "auto",
          minWidth: "max-content" 
        }}
      >
        <TableHead>
          {/* 📌 Column Headers */}
          <TableRow>
           {visibleColumns.map((column) => (
              <TableCell
                key={column.id}
                className={styles.headerCell}
                style={{
                  width: `${localColumnWidths[column.id]}px`,
                  minWidth: `${MIN_COLUMN_WIDTH}px`,
                  position: "relative",
                  fontSize: "12px", // ✅ Reduce text size
                  fontWeight: "700",
                  padding: "4px 6px 4px 12px", // ✅ Reduce padding to shrink row height
                  lineHeight: "2",
                }}
              >
                <TableSortLabel
                  active={orderBy === column.id}
                  direction={orderBy === column.id ? order : "asc"}
                  onClick={() => handleRequestSort(column.id)}
                  sx={{
                    "& .MuiTableSortLabel-icon": {
                      fontSize: "10px",
                    },
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      minWidth: 0,
                    }}
                  >
                    <span>{column.label}</span>

                    {getColumnHeaderHelperText(column) && (
                      <Tooltip
                        title={getColumnHeaderHelperText(column)}
                        placement="top"
                        arrow
                      >
                        <InfoOutlinedIcon
                          fontSize="inherit"
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                          onMouseDown={(event) => {
                            event.stopPropagation();
                          }}
                          sx={{
                            fontSize: "14px",
                            color: "text.secondary",
                            cursor: "help",
                          }}
                        />
                      </Tooltip>
                    )}
                  </span>
                </TableSortLabel>
                <div
                  className={styles.columnResizer}
                  onMouseDown={(e) => startResizing(column.id, e)}
                />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

<TableBody>
  {sortedRows
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    .map((row, index) => {
      // Derive a stable key for React so the DOM node for "row 0" does NOT get
      // reused for a different submission when the page/window changes.
      const rowKey = row?.id || row?.submissionId || `${page}-${index}`;

      return (
        <TableRow
          key={rowKey}
          className={styles.row}
          sx={{
            height: "auto",
            backgroundColor: (page * rowsPerPage + index) % 2 === 0 
              ? "#f5f5f5"  // ✅ Correct zebra pattern with pagination support
              : "inherit",
            '&:hover': {
              backgroundColor: "#ebebeb", // ✅ Subtle gray hover effect
            }
          }}
        >
{visibleColumns.map((column) => {
  // 0) System columns from ColumnDraftManager (ids like 'sys:*')
  if (isSystemId(String(column.id))) {
    const val  = resolveSystemValue(row, column.id); // reads row.id/submittedAt/etc.
    const text = (val ?? "N/A").toString();
    return (
      <TableCell
        key={column.id}
        className={styles.cell}
        data-column-id={column.id}
        sx={{ padding: "4px 6px", lineHeight: "1.5" }}
      >
        {highlightMatch(text, searchQuery)}
      </TableCell>
    );
  }


            // 1) Virtual column detection (reuse precomputed spec for speed/consistency)
            const spec = virtualSpecById.get(column.id) || {};

            // 2) Virtual roles
            if (spec.role) {
              // Provider-owned virtual output:
              // - Bitly Short URL today
              // - Future Bitly metrics / HubSpot / Slack / BigQuery outputs later
              //
              // This reads already-fetched row data only. It does not call provider
              // APIs, add Firestore listeners, or process integrations in the table.
              if (isProviderVirtualRole(spec.role)) {
                const providerColumn = {
                  ...column,
                  ...spec,
                  kind: column.kind || 'virtual',
                };
                const definition = getProviderColumnDefinition(providerColumn);
                const state = getProviderColumnDisplayState(row, providerColumn, definition);
                const displayText = state?.displayText || "";

                const copyEnabled =
                  providerColumn.copyEnabled ??
                  definition?.copyEnabled ??
                  false;

                let providerCellNode = displayText;

                if (state?.isPending) {
                  providerCellNode = (
                    <span
                      title={state.status || ""}
                      style={{ fontStyle: "italic", opacity: 0.72 }}
                    >
                      {displayText}
                    </span>
                  );
                } else if (state?.isError) {
                  providerCellNode = (
                    <span title={state.error || displayText}>
                      {displayText}
                    </span>
                  );
                } else if (copyEnabled && state?.canCopy && displayText) {
                  providerCellNode = renderCopyableText({
                    text: displayText,
                    row,
                    column: providerColumn,
                    copyable: true,
                  });
                } else if (typeof providerCellNode === "string") {
                  providerCellNode = highlightMatch(providerCellNode, searchQuery);
                }

                return (
                  <TableCell
                    key={column.id}
                    data-column-id={column.id}
                    className={styles.cell}
                    sx={{ padding: "4px 6px", lineHeight: "1.5" }}
                  >
                    {providerCellNode}
                  </TableCell>
                );
              }

              // Common lookups for asset-backed virtuals
              const linked = getLinkedForRow(row); // Map<attachUfi, entry> | null
              const aid    = spec.ufi ? extractFirstAssetId(row, spec.ufi) : null;
              const cached = aid ? getPreview(aid) : null;

              let cellNode = "";

  // thumb: show preview image if ready; else gray square
  if (spec.role === "thumb") {
    if (cached?.previewUrl) {
      cellNode = (
        <img
          src={cached.previewUrl}
          alt=""
          style={{
            width: thumbSize,
            height: thumbSize,
            objectFit: "cover",
            borderRadius: 2,
          }}
          title={aid || ""}
          aria-label="asset thumbnail"
        />
      );
    } else {
      cellNode = (
        <span
          title={aid || ""}
          aria-label="asset thumbnail loading"
          style={{
            width: thumbSize,
            height: thumbSize,
            display: "inline-block",
            background: "#eee",
            borderRadius: 2,
          }}
        />
      );
    }
  }


              // version: prefer linked.versionNumber, fallback to cell payload
              if (spec.role === "version") {
                let v = "";
                const lin = linked?.get(spec.ufi);
                if (lin && typeof lin.versionNumber !== "undefined") {
                  v = String(lin.versionNumber ?? "");
                } else if (aid && cached?.meta) {
                  // fallback to preview meta if you ever promote version here
                  v = String(cached.meta?.versionNumber ?? cached.meta?.version ?? "");
                } else {
                  // brute fallback: unwrap the cell value
                  const raw = row?.responses?.[spec.ufi];
                  const x   = unwrapValue(raw && raw.value ? raw.value : raw);
                  if (Array.isArray(x)) {
                    const first = x.find(it => it && typeof it === "object" && (it.assetId || it.id));
                    v = String(first?.versionNumber ?? first?.version ?? "");
                  } else if (x && typeof x === "object") {
                    v = String(x.versionNumber ?? x.version ?? "");
                  }
                }
                cellNode = v;
              }

              // assetType: prefer linked.assetType then fallback to payload
              if (spec.role === "assetType") {
                let v = "";
                const lin = linked?.get(spec.ufi);
                if (lin && typeof lin.assetType !== "undefined") {
                  v = String(lin.assetType ?? "");
                } else if (aid && cached?.meta) {
                  v = String(cached.meta?.assetType ?? "");
                } else {
                  const raw = row?.responses?.[spec.ufi];
                  const x   = unwrapValue(raw && raw.value ? raw.value : raw);
                  if (Array.isArray(x)) {
                    const first = x.find(it => it && typeof it === "object" && (it.assetId || it.id));
                    v = String(first?.assetType ?? "");
                  } else if (x && typeof x === "object") {
                    v = String(x.assetType ?? "");
                  }
                }
                cellNode = v;
              }

              // aufField: prefer linked.aufResponses[aufUfi], fallback to preview meta
              if (spec.role === "aufField" && spec.aufUfi) {
                let v = "";
                const lin = linked?.get(spec.ufi);
                if (lin?.aufResponses && spec.aufUfi in lin.aufResponses) {
                  const raw = lin.aufResponses[spec.aufUfi];
                  if (Array.isArray(raw))      v = raw.filter(Boolean).join(", ");
                  else if (raw != null)        v = String(raw);
                } else if (aid && cached?.meta) {
                  const rv = cached.meta?.responses?.[spec.aufUfi]?.value;
                  if (Array.isArray(rv))       v = rv.filter(Boolean).join(", ");
                  else if (rv != null)         v = String(rv);
                }
                cellNode = v;
              }

              // assetId: print the first asset id
              if (spec.role === "assetId") {
                cellNode = aid || "";
              }

              // Convert plain strings into highlighted nodes; keep nodes (img/span) as-is
              const maybeHighlighted =
                typeof cellNode === "string"
                  ? highlightMatch(cellNode, searchQuery)
                  : cellNode;


              return (
                <TableCell
                  key={column.id}
                  data-column-id={column.id}
                  className={styles.cell}
                  sx={{ padding: "4px 6px", lineHeight: "1.5" }}
                >
                {maybeHighlighted}
                </TableCell>
              );
            }


// SPECIAL CASE: base attach/image UFIs → render micro thumbnail (mirror Draft)
const fType = ufiToType[column.id];
if (fType === "attach" || fType === "image") {
  const aid = extractFirstAssetId(row, column.id);
  const cached = aid ? getPreview(aid) : null;
  const url = cached?.previewUrl || null;

  return (
    <TableCell
      key={column.id}
      className={styles.cell}
      data-column-id={column.id}
      sx={{ padding: "4px 6px", lineHeight: "1.5" }}
    >
      {url ? (
        <img
          src={url}
          alt=""
          style={{
            width: thumbSize,
            height: thumbSize,
            objectFit: "cover",
            borderRadius: 2,
          }}
          title={aid || ""}
          aria-label="asset thumbnail"
        />
      ) : (
        <span
          title={aid || ""}
          aria-label="asset thumbnail loading"
          style={{
            width: thumbSize,
            height: thumbSize,
            display: "inline-block",
            background: "#eee",
            borderRadius: 2,
          }}
        />
      )}
    </TableCell>
  );
}



            // 3) Base fields (non-virtual): normalized scalar (mirror Draft's unwrap)
            const rawCell = row?.responses?.[column.id];
            const rawVal  = (rawCell && typeof rawCell === "object" && "value" in rawCell) ? rawCell.value : rawCell;
            const normalizedVal = unwrapValue(rawVal);
            let extractedValue = normalizedVal == null ? "N/A" : (
              typeof normalizedVal === "object"
                ? (typeof normalizedVal?.value !== "object" && normalizedVal?.value != null ? String(normalizedVal.value) : "")
                : String(normalizedVal)
            );

            if (column.id === "createdAt") {
              const createdAtRaw = row?.responses?.createdAt?.value?.value;
              extractedValue = createdAtRaw
                ? new Date(createdAtRaw).toLocaleString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit"
                  })
                : "N/A";
            }
            const isUrlCell = isHttpUrlValue(extractedValue);
            const shouldCopyCell =
              column.id === "displayInCardMessage" ||
              isUrlCell;

            return (
              <TableCell
                key={column.id}
                className={`${styles.cell} ${shouldCopyCell ? styles.copyEnabled : ""}`}
                data-column-id={column.id}
                sx={{
                  padding: "4px 6px",
                  lineHeight: "1.5",
                  cursor: shouldCopyCell ? "pointer" : "default",
                }}
              >
                {shouldCopyCell ? (
                  renderCopyableText({
                    text: extractedValue,
                    row,
                    column,
                    forceCopy: column.id === "displayInCardMessage",
                    copyable: isUrlCell,
                  })
                ) : (
                  highlightMatch(String(extractedValue ?? ""), searchQuery)
                )}
              </TableCell>
            );
          })}
        </TableRow>
      );
    })}

  {/* ✅ Add Empty Rows to Respect Rows Per Page Selection */}
  {Array.from({
    length: Math.max(
      0,
      rowsPerPage -
        renderRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length
    )
  }).map((_, idx) => (

    <TableRow
      key={`empty-${idx}`}
      sx={{
        height: "40px",
        backgroundColor: (page * rowsPerPage + renderRows.length + idx) % 2 === 0
          ? "#f5f5f5"
          : "inherit"

      }}
    >
      {visibleColumns
        .map((column) => (
          <TableCell key={`empty-cell-${column.id}-${idx}`} />
        ))}
    </TableRow>
  ))}
</TableBody>

      </Table>
    </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalRowsForPagination}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
  </Paper>
);

};

export default SubmissionTable;
